import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, PieChart, Pie } from 'recharts';
import { toast } from 'sonner';
import { Loader2, Euro, TrendingUp, AlertCircle, RefreshCw, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

const ZONE_COLORS = {
  'Congelateur': '#3b82f6',
  'Frigo': '#06b6d4',
  'Saladette': '#10b981',
  'Reserve Sec': '#f59e0b',
  'Bubble Tea & Emballages': '#a855f7',
  'Sirops': '#ec4899',
  'Autre': '#64748b',
};

export function ValorisationTab() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/inventaire/valorisation');
      setData(res.data);
    } catch {
      toast.error('Erreur chargement valorisation');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetch(); }, [fetch]);

  if (loading || !data) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  }

  const matchPctColor = data.pct_matching >= 70 ? 'text-green-600' : data.pct_matching >= 40 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-4" data-testid="valorisation-tab">
      {/* Top stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-2 border-emerald-300 bg-emerald-50/40">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Valeur totale du stock</span>
              <Euro className="w-4 h-4 text-emerald-600" />
            </div>
            <p className="text-3xl font-bold tabular-nums text-emerald-700" data-testid="total-stock-value">
              {data.total_value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Calcul via Mercuriale</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Articles valorisés</span>
              <TrendingUp className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold tabular-nums" data-testid="matched-count">
              {data.nb_matched} / {data.nb_total}
            </p>
            <p className={`text-xs font-semibold mt-0.5 ${matchPctColor}`}>
              {data.pct_matching}% de couverture
            </p>
          </CardContent>
        </Card>

        <Card className={data.nb_unmatched > 0 ? 'border-amber-300' : ''}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Articles non matchés</span>
              <AlertCircle className={`w-4 h-4 ${data.nb_unmatched > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
            </div>
            <p className="text-2xl font-bold tabular-nums" data-testid="unmatched-count">
              {data.nb_unmatched}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5">À ajouter à la Mercuriale</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 pb-3 flex flex-col items-start gap-1">
            <span className="text-xs text-muted-foreground">Mercuriale</span>
            <Button asChild variant="outline" size="sm" className="gap-1 mt-1" data-testid="goto-mercuriale">
              <Link to="/mercuriale"><Search className="w-3 h-3" /> Voir catalogue</Link>
            </Button>
            <Button variant="ghost" size="sm" className="gap-1 h-7 text-xs" onClick={fetch} data-testid="refresh-valorisation">
              <RefreshCw className="w-3 h-3" /> Actualiser
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* By zone */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Répartition par zone</CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_zone.filter(z => z.value > 0).length === 0 ? (
              <p className="text-center py-8 text-muted-foreground text-sm">Aucune valorisation possible — ajoute des produits à la Mercuriale</p>
            ) : (
              <div className="space-y-3">
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.by_zone.filter(z => z.value > 0)}
                        dataKey="value"
                        nameKey="zone"
                        cx="50%" cy="50%" outerRadius={70} innerRadius={35}
                        label={(entry) => `${entry.zone.slice(0, 12)} ${entry.value.toFixed(0)}€`}
                        labelLine={false}
                        fontSize={10}
                      >
                        {data.by_zone.filter(z => z.value > 0).map((z, i) => (
                          <Cell key={i} fill={ZONE_COLORS[z.zone] || '#64748b'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${v.toFixed(2)} €`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <Table>
                  <TableBody>
                    {data.by_zone.map(z => (
                      <TableRow key={z.zone}>
                        <TableCell className="py-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded" style={{ backgroundColor: ZONE_COLORS[z.zone] || '#64748b' }} />
                            <span className="text-sm">{z.zone}</span>
                          </div>
                        </TableCell>
                        <TableCell className="py-1.5 text-right text-xs text-muted-foreground">
                          {z.nb_matched}/{z.nb_items} matchés
                        </TableCell>
                        <TableCell className="py-1.5 text-right font-bold tabular-nums">
                          {z.value.toFixed(2)} €
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top valorisés */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top 10 articles par valeur</CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_value.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground text-sm">Aucune donnée</p>
            ) : (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.top_value.slice(0, 10)} layout="vertical" margin={{ left: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                    <XAxis type="number" fontSize={10} unit="€" />
                    <YAxis type="category" dataKey="ingredient" fontSize={9} width={120}
                      tickFormatter={(v) => v.length > 18 ? v.slice(0, 16) + '…' : v} />
                    <Tooltip formatter={(v) => `${v.toFixed(2)} €`} />
                    <Bar dataKey="valeur" radius={[0, 4, 4, 0]}>
                      {data.top_value.slice(0, 10).map((t, i) => (
                        <Cell key={i} fill={ZONE_COLORS[t.zone] || '#10b981'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top value detail table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Détail top valorisations</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Article</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead className="text-right">Stock</TableHead>
                <TableHead>Match Mercuriale</TableHead>
                <TableHead className="text-center">Score</TableHead>
                <TableHead className="text-right">Valeur</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.top_value.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                  Aucune valorisation disponible. Importe des factures dans la Mercuriale pour activer le calcul.
                </TableCell></TableRow>
              ) : data.top_value.map((t, i) => (
                <TableRow key={i} data-testid={`top-row-${i}`}>
                  <TableCell className="font-medium">{t.ingredient}</TableCell>
                  <TableCell>
                    <Badge variant="outline" style={{ borderColor: ZONE_COLORS[t.zone] || '#94a3b8' }}>
                      {t.zone}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums">
                    {t.stock} {t.unite}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {t.match_nom || '—'}
                    {t.match_fournisseur && <span className="block text-[10px]">{t.match_fournisseur}</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {t.match_score >= 0.9 ? <Badge className="bg-green-100 text-green-700">Exact</Badge> :
                     t.match_score >= 0.7 ? <Badge className="bg-blue-100 text-blue-700">Fort</Badge> :
                     t.match_score >= 0.5 ? <Badge className="bg-amber-100 text-amber-700">Approx.</Badge> :
                     <Badge variant="outline" className="text-muted-foreground">—</Badge>}
                  </TableCell>
                  <TableCell className="text-right font-bold tabular-nums">
                    {t.valeur.toFixed(2)} €
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Unmatched items */}
      {data.unmatched.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Articles sans prix ({data.unmatched.length})
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Ces articles ne sont pas dans la Mercuriale. Importe une facture ou ajoute-les manuellement pour calculer leur valeur.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Article</TableHead>
                  <TableHead>Zone</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.unmatched.map((u, i) => (
                  <TableRow key={i} data-testid={`unmatched-${i}`}>
                    <TableCell className="font-medium">{u.ingredient}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{u.zone}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{u.stock} {u.unite}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
